import { serialiseJql } from '@atlassian/jira-business-filters/src/controllers/utils.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	createStore,
	createHook,
	type StoreActionApi,
	createContainer,
} from '@atlassian/react-sweet-state';
import { fg } from '@atlassian/jira-feature-gating';
import type { MappedRule, RuleJqlInput } from '../../common/types.tsx';

type FormatRules = {
	rowRules: MappedRule[];
	cellRulesByFieldId: { [fieldId: string]: MappedRule[] };
};

type State = FormatRules & {
	issueIdRowRuleMatched: { [issueId: string]: MappedRule };
	rulesJqlInput: RuleJqlInput;
};

type Actions = typeof actions;

const initialState: State = {
	rowRules: [],
	cellRulesByFieldId: {},
	issueIdRowRuleMatched: {},
	rulesJqlInput: {},
};

const actions = {
	setFormatRules:
		(rules: FormatRules) =>
		({ setState }: StoreActionApi<State>) => {
			const newState = {
				...rules,
			};
			setState(newState);
		},
	setRulesForJqlInput:
		(input: RuleJqlInput) =>
		({ setState }: StoreActionApi<State>) => {
			const newState = {
				rulesJqlInput: input,
			};
			setState(newState);
		},
	setMatchedRowRule:
		({ rule, issueId }: { rule: MappedRule; issueId: string }) =>
		({ setState, getState }: StoreActionApi<State>) => {
			const currentState = getState();
			const newState = {
				issueIdRowRuleMatched: {
					...currentState.issueIdRowRuleMatched,
					[issueId]: rule,
				},
			};
			setState(newState);
		},
	removeMatchedRowRule:
		({ ruleId }: { ruleId: string }) =>
		({ setState, getState }: StoreActionApi<State>) => {
			const { issueIdRowRuleMatched } = getState();

			const rowRuleMatched = Object.entries(issueIdRowRuleMatched).filter(
				([, value]) => value.id !== ruleId,
			);
			const newIssueIdRowRuleMatched = Object.fromEntries(rowRuleMatched);
			setState({
				issueIdRowRuleMatched: newIssueIdRowRuleMatched,
			});
		},
};

const oldStore = createStore<State, Actions>({
	initialState,
	actions,
	name: 'JWMFormatRulesOld',
});

/**
 * @deprecated Any changes made to this file should also be made to @atlassian/jira-issue-navigator-conditional-formatting-sidebar
 */
export const FormatRulesContainer = createContainer();

const Store = createStore<State, Actions>({
	initialState,
	actions,
	name: 'JWMFormatRules',
	containedBy: FormatRulesContainer,
});

const useFormatRulesStoreOld = createHook(oldStore);
const useFormatRulesStoreNew = createHook(Store);

/**
 * @deprecated Any changes made to this file should also be made to @atlassian/jira-issue-navigator-conditional-formatting-sidebar
 */
export const useFormatRulesStore = () =>
	fg('jira_list_conditional_formatting')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useFormatRulesStoreNew()
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			useFormatRulesStoreOld();

const getSerialisedJql = (state: State) => serialiseJql(state.rulesJqlInput);

const useFormatRulesSerialisedJqlOld = createHook(oldStore, { selector: getSerialisedJql });
const useFormatRulesSerialisedJqlNew = createHook(Store, { selector: getSerialisedJql });

/**
 * @deprecated Any changes made to this file should also be made to @atlassian/jira-issue-navigator-conditional-formatting-sidebar
 */
export const useFormatRulesSerialisedJql = () =>
	fg('jira_list_conditional_formatting')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useFormatRulesSerialisedJqlNew()
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			useFormatRulesSerialisedJqlOld();
